import React, { useState } from "react";
import { Outlet, useNavigate, Navigate } from "react-router-dom";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import Sidebar, {
  DRAWER_WIDTH_OPEN,
  DRAWER_WIDTH_CLOSED,
} from "../../components/Sidebar";
import InnerHeader from "../../components/InnerHeader";
import PageLoader from "../../components/PageLoader";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Briefcase,
  CreditCard,
  FileText,
  Star,
  FileText as FileIcon,
  ClipboardList,
  History,
} from "lucide-react";

const AdminLayout = () => {
  const { user, logout, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (loading) {
    return <PageLoader />;
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const navItems = [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/users", label: "Users", icon: Users },
    { path: "/admin/courses", label: "Courses", icon: BookOpen },
    { path: "/admin/brokers", label: "Brokers", icon: Briefcase },
    { path: "/admin/job-openings", label: "Job Openings", icon: ClipboardList },
    { path: "/admin/resumes", label: "Resumes", icon: FileIcon },
    {
      path: "/admin/subscription-plans",
      label: "Subscription plans",
      icon: CreditCard,
    },
    { path: "/admin/payments", label: "Payment History", icon: History },
    { path: "/admin/reviews", label: "Reviews & Ratings", icon: Star },
    { path: "/admin/cms", label: "CMS Pages", icon: FileText },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "grey.100",
      }}
    >
      <Sidebar
        open={drawerOpen}
        onToggle={() => setDrawerOpen((o) => !o)}
        brandTitle="Market Mavericks"
        brandSubtitle="Admin Panel"
        brandLink="/admin"
        navItems={navItems}
        brandIcon={LayoutDashboard}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",

          // responsive fix
          marginLeft: isMobile
            ? 0
            : drawerOpen
              ? `${DRAWER_WIDTH_OPEN}px`
              : `${DRAWER_WIDTH_CLOSED}px`,

          transition: (theme) =>
            theme.transitions.create("margin-left", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),

          bgcolor: "grey.50",
        }}
        style={{ 
          '--sidebar-width': isMobile ? '0px' : (drawerOpen ? `${DRAWER_WIDTH_OPEN}px` : `${DRAWER_WIDTH_CLOSED}px`) 
        }}
      >
        <InnerHeader
          user={user}
          profileLink="/user/profile"
          onLogout={handleLogout}
          showProfileUI
          onToggleSidebar={() => setDrawerOpen((o) => !o)}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: { xs: 2, lg: 3 },
            position: "relative",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
